import React, { useState, useRef, useEffect } from 'react';
import { IoSend } from 'react-icons/io5';
import { FaDownload } from "react-icons/fa";
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const Chart = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([
    { text: "Assalam-o-Alaikum! I am the HMS Assistant. Do you want to ask something?", sender: "bot" }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const printRef = useRef();

  const toggleChat = () => setIsChatOpen(prev => !prev);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // EXACT SAME UI IN PDF — 100% MATCH
  const downloadChat = async () => {
    const element = printRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#fff'
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210;
    const pageHeight = 295;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save(`HMS_Chat_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.pdf`);
  };

  // Send message
  const sendMessage = async () => {
    if (!inputValue.trim()) return;
    const userText = inputValue.trim();
    setMessages(prev => [...prev, { text: userText, sender: "user" }]);
    setInputValue("");
    setIsTyping(true);

    try {
      const res = await fetch('https://hms.backend.hiragroupcloud.com/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userText })
      });
      const data = await res.json();

      setTimeout(() => {
        setMessages(prev => [...prev, {
          text: data.reply || "Sorry, kuch gadbad ho gayi.",
          sender: "bot"
        }]);
        setIsTyping(false);
      }, 800);
    } catch (err) {
      setTimeout(() => {
        setMessages(prev => [...prev, { text: "Server error!", sender: "bot" }]);
        setIsTyping(false);
      }, 1000);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {/* Hidden div for PDF - exact same look */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={printRef} style={{
          width: '800px', padding: '40px', background: 'white',
          fontFamily: 'Segoe UI, sans-serif', boxSizing: 'border-box'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea, #004aad)',
            color: 'white', padding: '30px', textAlign: 'center',
            borderRadius: '20px 20px 0 0', margin: '-40px -40px 40px -40px'
          }}>
            <img src="/logo.png" alt="HMS" style={{
              width: '90px', height: '90px', borderRadius: '50%',
              border: '5px solid white', marginBottom: '15px'
            }} />
            <h1 style={{ margin: '10px 0', fontSize: '32px' }}>HMS AI Assistant</h1>
            <p style={{ margin: 0, fontSize: '18px' }}>Chat History</p>
            <p style={{ margin: '10px 0 0', opacity: 0.9 }}>
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} • {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
            </p>
          </div>

          {/* Messages */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', padding: '0 30px' }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                alignSelf: msg.sender === "user" ? 'flex-end' : 'flex-start',
                maxWidth: '80%'
              }}>
                <div style={{
                  backgroundColor: msg.sender === "user" ? '#004aad' : '#fff',
                  color: msg.sender === "user" ? 'white' : '#333',
                  padding: '14px 20px',
                  borderRadius: '20px',
                  fontSize: '16px',
                  lineHeight: '1.5',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.1)',
                  borderBottomLeftRadius: msg.sender === "bot" ? '4px' : '20px',
                  borderBottomRightRadius: msg.sender === "user" ? '4px' : '20px',
                  whiteSpace: 'pre-line'
                }}>
                  {msg.text}
                </div>
              </div>
            ))}
          </div>

          <div style={{
            textAlign: 'center', padding: '40px', color: '#888',
            fontSize: '14px', marginTop: '50px', borderTop: '2px solid #eee'
          }}>
            <p>Powered by HMS AI Assistant • © 2025</p>
          </div>
        </div>
      </div>

      {/* Actual Chat UI */}
      <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
        <div style={{
          position: 'absolute', bottom: '70px', right: '0', width: '380px',
          height: isChatOpen ? '680px' : '0', backgroundColor: '#fff',
          borderRadius: '20px', boxShadow: '0 15px 40px rgba(0,0,0,0.25)',
          overflow: 'hidden', transition: 'all 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
          transform: isChatOpen ? 'scale(1)' : 'scale(0.8)', opacity: isChatOpen ? 1 : 0,
          pointerEvents: isChatOpen ? 'all' : 'none', border: '1px solid #e0e0e0',
          display: 'flex', flexDirection: 'column'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #667eea 0%, #004aad 100%)',
            color: 'white', padding: '16px 18px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img src="/logo.png" alt="HMS" style={{
                width: '48px', height: '48px', borderRadius: '50%',
                backgroundColor: 'white', padding: '4px', objectFit: 'contain'
              }} />
              <div>
                <div style={{ fontWeight: 'bold', fontSize: '16px' }}>HMS AI Assistant</div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>Online • Real AI</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '16px' }}>
              <button onClick={downloadChat} title="Download PDF" style={{
                background: 'none', border: 'none', color: 'white',
                padding: '8px', borderRadius: '50%', cursor: 'pointer'
              }}>
                <FaDownload size={24} />
              </button>
              <button onClick={toggleChat} style={{
                background: 'none', border: 'none', color: 'white',
                fontSize: '34px', cursor: 'pointer'
              }}>×</button>
            </div>
          </div>

          {/* Messages Area */}
          <div style={{
            flex: 1, padding: '18px', overflowY: 'auto', backgroundColor: '#f5f7fb',
            display: 'flex', flexDirection: 'column', gap: '14px'
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                alignSelf: msg.sender === "user" ? 'flex-end' : 'flex-start',
                backgroundColor: msg.sender === "user" ? '#004aad' : '#fff',
                color: msg.sender === "user" ? 'white' : '#333',
                padding: '12px 18px', borderRadius: '20px', maxWidth: '82%',
                fontSize: '14.5px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                borderBottomLeftRadius: msg.sender === "bot" ? '4px' : '20px',
                borderBottomRightRadius: msg.sender === "user" ? '4px' : '20px',
                whiteSpace: 'pre-line', lineHeight: '1.5'
              }}>
                {msg.text}
              </div>
            ))}
            {isTyping && (
              <div style={{
                alignSelf: 'flex-start', backgroundColor: '#fff', padding: '12px 18px',
                borderRadius: '20px', borderBottomLeftRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', gap: '7px' }}>
                  <span style={{ 
                    width: '9px', height: '9px', background: '#999', borderRadius: '50%',
                    animation: 'bounce 1.4s infinite'
                  }}></span>
                  <span style={{ 
                    width: '9px', height: '9px', background: '#999', borderRadius: '50%',
                    animation: 'bounce 1.4s infinite 0.2s'
                  }}></span>
                  <span style={{ 
                    width: '9px', height: '9px', background: '#999', borderRadius: '50%',
                    animation: 'bounce 1.4s infinite 0.4s'
                  }}></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div style={{
            padding: '16px 18px', backgroundColor: '#fff', borderTop: '1px solid #eee',
            display: 'flex', alignItems: 'center', gap: '12px'
          }}>
            <input
              type="text" placeholder="Ask anything… " value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              style={{
                flex: 1, padding: '14px 18px', border: '1px solid #ddd',
                borderRadius: '30px', fontSize: '15px', outline: 'none'
              }}
            />
            <button onClick={sendMessage} style={{
              backgroundColor: '#004aad', color: 'white', border: 'none',
              width: '48px', height: '48px', borderRadius: '50%',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <IoSend size={20} />
            </button>
          </div>
        </div>

        {/* Floating Button */}
        <div onClick={toggleChat} style={{
          width: '64px', height: '64px', backgroundColor: '#004aad',
          borderRadius: '50%', display: 'flex', alignItems: 'center',
          justifyContent: 'center', boxShadow: '0 6px 20px rgba(0,74,173,0.5)',
          cursor: 'pointer'
        }}>
          <svg style={{ width: '36px', height: '36px', fill: 'white' }} viewBox="0 0 24 24">
            <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z" />
          </svg>
        </div>
      </div>

      {/* Animation styles using style tag without jsx attribute */}
      <style>{`
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-12px); }
        }
      `}</style>
    </>
  );
};

export default Chart;